import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Form, Formik } from 'formik';
import * as Yup from 'yup';
import dayjs from 'dayjs';

import {
  BaseBox,
  BaseBoxHeading,
  BaseButtonFill,
  BaseIcon,
  BaseLoader,
  BaseStatus,
  BaseTable,
  RequestStepWrapper,
  SelectField,
} from 'bxcommon';
import { RequestDataWrapper } from 'bxcommon/components';
import { createUploadBox, requiredFilesValidator } from 'bxcommon/helpers/form.helper';
import errorService from 'bxcommon/helpers/errors.helper';
import { STATUS } from 'bxcommon/constants/status.constant';

import { ProspectusHeader } from '../ProspectusHeader/ProspectusHeader';
import AppConfig from 'bxcommon/providers/AppConfig';
import { RequestContext } from 'bxcommon/context/Request.context';
import { errorToast, successToast } from 'bxcommon/store/slices/toaster.slice';
import config from '../../prospectus.config';
import { getAddendumList, deleteAdditionalFiles, patchAdditionalFiles } from '../../common/requests';

import './Addendum.scss';

const { maxFileSize, ADDITIONAL_FILE_TYPES } = config;

export const Addendum = () => {
  const { t } = useTranslation();
  const { t: common } = useTranslation('common');

  const navigate = useNavigate();

  const { request, resources } = useContext(RequestContext);
  const addendum_type = resources?.addendum_type;

  const [list, setList] = useState([]);
  const [listLoading, setListLoading] = useState(true);
  const [disabled, setDisabled] = useState(false);
  const formRef = useRef(null);

  const blockUpload = useMemo(() => {
    return list.some((entry) => entry.status === STATUS.IN_REVIEW);
  }, [list]);

  const showDeleteButton = useMemo(() => {
    return list.some((entry) => entry.status === STATUS.DRAFT);
  }, [list]);

  useEffect(() => {
    if (request.case_id && request.status !== STATUS.APPROVED) {
      navigate('/list');
    }
  }, [navigate, request.case_id, request.status]);

  const getList = useCallback(async () => {
    try {
      setList(await getAddendumList(request.case_id));
    } catch (e) {
      errorService.showBackendErrors(e);
    } finally {
      setListLoading(false);
    }
  }, [request.case_id]);

  useEffect(() => {
    if (request.case_id) {
      // oxlint-disable-next-line react-hooks/set-state-in-effect
      getList();
    }
  }, [request, getList]);

  const refetchData = (refetch) => {
    if (refetch) {
      setListLoading(true);
      getList();
    }
  };

  const onDeleteClick = async () => {
    try {
      await deleteAdditionalFiles(request.case_id, ADDITIONAL_FILE_TYPES.ADDENDUM);
      setList([]);
      successToast(common('request.form.common.allDraftFilesRemoved'), common('request.form.common.success'));
    } catch (e) {
      errorToast(common(`request.form.errors.defaultErrorDescription`), common(`request.errors.error`));
    }
  };

  const uploadInputRef = useRef(null);
  // oxlint-disable-next-line react-hooks/refs
  const renderUploadBox = createUploadBox(() => {}, formRef, refetchData);
  const initialValues = {
    uploadType: 5,
    files: [],
  };

  const validationSchema = Yup.object().shape({
    uploadType: Yup.mixed().required(common('errors.requiredField')),
    files: requiredFilesValidator(),
  });

  const onFormSubmit = async () => {
    setDisabled(true);
    let results = await uploadInputRef.current.manualUpload(ADDITIONAL_FILE_TYPES.ADDENDUM);
    const withError = results.some((file) => file.status === STATUS.ERROR);

    if (withError) {
      await deleteAdditionalFiles(request.case_id, ADDITIONAL_FILE_TYPES.ADDENDUM);
      errorToast(
        common(`request.form.errors.upload.backendErrorDuringUpload.description`),
        common(`request.form.errors.upload.backendErrorDuringUpload.title`)
      );
    } else {
      let finalList = await patchAdditionalFiles(request.case_id, ADDITIONAL_FILE_TYPES.ADDENDUM);
      successToast(common('request.form.common.allFilesUploadedSuccessfully'), common('request.form.common.success'));
      setList([...list, ...finalList]);
    }
    setDisabled(false);
  };

  const splitNameAndRemoveUnnecessaryElements = (name) => {
    const splitName = name.split('_');
    splitName.splice(0, 5);
    return splitName;
  };

  const preparingToDisplay = (addendums) => {
    const sorted = addendums.sort((a, b) => (a.created_at > b.created_at ? -1 : 1));
    const result = [];
    let prevFileIdx = null;
    let index = 0;
    sorted.forEach((current, idx) => {
      const currFileIdx = splitNameAndRemoveUnnecessaryElements(current.name).pop();
      if (idx === 0) {
        result[index] = [current];
        prevFileIdx = currFileIdx;
        return;
      }
      if (!currFileIdx || !prevFileIdx || parseInt(currFileIdx) >= parseInt(prevFileIdx)) {
        index++;
      }
      result[index] ? result[index].push(current) : (result[index] = [current]);
      prevFileIdx = currFileIdx;
    });
    return result;
  };

  if (!addendum_type) return null;

  return (
    <ProspectusHeader>
      <div className="addendum">
        <RequestDataWrapper redirect={'/list'} requestId={request.case_id}>
          <RequestStepWrapper noSteps hidden goBack={`../details/${request.case_id}`}>
            <BaseLoader loading={listLoading} large>
              <BaseBox className="request-form__box" lifted>
                <BaseBoxHeading>{t('addendum.heading')}</BaseBoxHeading>
                {!blockUpload && (
                  <Formik
                    innerRef={formRef}
                    initialValues={initialValues}
                    onSubmit={() => onFormSubmit()}
                    validationSchema={validationSchema}
                  >
                    {({ values }) => (
                      <Form className="form-container">
                        <div className="col-10">
                          <div className="row">
                            <SelectField
                              name="uploadType"
                              label={t('addendum.uploadType')}
                              options={addendum_type.data}
                            />
                          </div>
                        </div>
                        <div className="row">
                          <div className="col-10">
                            <div className="addendum__upload">
                              {renderUploadBox({
                                name: 'files',
                                conditions: common('request.form.common.fileDefaultsPDF'),
                                type: values.uploadType,
                                maxFileSize,
                                config: {
                                  shouldUploadManually: true,
                                  multiple: true,
                                },
                                uploadInputRef,
                              })}
                            </div>

                            <BaseButtonFill
                              type="submit"
                              className="base-upload-box__submit-button"
                              loading={disabled}
                              disabled={disabled}
                              data-testid="submit-button"
                            >
                              {common('submit')}
                            </BaseButtonFill>

                            {showDeleteButton && (
                              <BaseButtonFill
                                type="button"
                                className="base-upload-box__submit-button"
                                onClick={onDeleteClick}
                              >
                                {t('addendum.removeFilesButton')}
                              </BaseButtonFill>
                            )}
                          </div>
                        </div>
                      </Form>
                    )}
                  </Formik>
                )}

                {blockUpload && (
                  <div className="addendum__info-wrapper">
                    <BaseIcon icon="info" />
                    <p
                      className="copy copy--color-dark copy--small addendum__info"
                      data-testid="addendum-upload-not-allowed"
                    >
                      {t('addendum.uploadNotAllowed')}
                    </p>
                  </div>
                )}
              </BaseBox>
              {list.length ? (
                <BaseTable labels={t('addendum.formLabels', { returnObjects: true })}>
                  {preparingToDisplay(list).map((addendum, idx) => (
                    <tr key={idx} className="addendum__table-item" data-testid="addendum-table-item">
                      <td className="addendum__table--with-icon">
                        {addendum.map((file, idx2) => (
                          <div key={idx2}>
                            <BaseIcon icon="file-type-pdf" />
                            <a href={file.protected_url} target="_blank" rel="noopener noreferrer">
                              <span>{file.name}</span>
                            </a>
                          </div>
                        ))}
                      </td>
                      <td>
                        {addendum[0].created_at && dayjs(addendum[0].created_at).format(AppConfig.dayMonthHourFormat)}
                      </td>
                      <td>
                        <BaseStatus status={addendum[0].status} />
                      </td>
                    </tr>
                  ))}
                </BaseTable>
              ) : (
                <div className="addendum__info-wrapper">
                  <BaseIcon icon="info" />
                  <p className="copy copy--color-dark copy--small addendum__info" data-testid="addendum-empty-list">
                    {t('addendum.emptyList')}
                  </p>
                </div>
              )}
            </BaseLoader>
          </RequestStepWrapper>
        </RequestDataWrapper>
      </div>
    </ProspectusHeader>
  );
};

export default Addendum;
